"use client";

import { useEffect, useState, memo, Component, ReactNode } from "react";
import { createLogger } from "@/lib/logger";
import HorizontalScroll from "@/app/components/HorizontalScroll";
import Link from "next/link";

const logger = createLogger("CopyTradingSection");
import { formatCompactCurrency } from "@/lib/format";
import LeaderAvatar from "@/app/components/LeaderAvatar";
import Tooltip from "@/app/components/Tooltip";
import {
  FiUser,
  FiInfo,
  FiAlertCircle,
  FiWifi,
  FiWifiOff,
  FiSliders,
} from "react-icons/fi";
import { useCopytradingSocket } from "@/hooks/useCopytradingSocket";
import { authFetch } from "@/lib/authFetch";
import { useT } from "@/hooks/useT";

// Error Boundary to catch rendering errors
class ErrorBoundary extends Component<{ children: ReactNode; fallback: ReactNode }, { hasError: boolean }> {
  constructor(props: { children: ReactNode; fallback: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    logger.error("CopyTradingSection Error", { error: error.message, componentStack: errorInfo.componentStack });
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

interface FollowedLeader {
  id: string;
  leader_id: string;
  user_id: string;
  copy_enabled: boolean;
  copy_mode: "fixed" | "percentage" | "balance_percentage";
  base_stake_usd: number;
  stake_percentage: number;
  max_stake_per_trade_usd: number;
  created_at: string;
  polymarket_address: string;
  polymarket_username: string;
  custom_label?: string | null;
  profile_image_url: string;
  leader_volume: number;
  leader_win_rate: number;
  // Our stats (from our copytrades)
  leader_our_volume: number;
  leader_our_win_rate: number | null;
  leader_our_wins: number;
  leader_our_losses: number;
  last_trade_detected_at: string;
  total_copied_trades: number;
  total_volume_usd: number;
  total_realized_pnl_usd: number;
  // 2026-05-22: per-leader PnL + open positions for the new
  // mini-stats row inside each leader card.
  total_unrealized_pnl_usd?: number;
  total_cost_basis_usd?: number;
  open_positions_count?: number;
}


// 2026-05-26: this section was slimmed down to be a financial summary
// rather than a management surface. The full pause/resume/edit/unfollow
// controls + per-leader config rows now ONLY live in /copytrading; this
// view keeps the headline KPIs (5 stat cards above) and a compact list
// of follows where each row is just avatar + name + per-leader PnL +
// a "Manage" link routing to /copytrading. The earlier 200-line
// LeaderCard component and its toggle/unfollow handlers were removed
// to eliminate the dual-source-of-truth bug class (changes in
// /copytrading > Traders UI would silently leave this view stale).
interface CompactLeaderRowProps {
  leader: FollowedLeader;
  t: (key: string, vars?: Record<string, string | number>) => string;
}

const CompactLeaderRow = memo(function CompactLeaderRow({ leader, t }: CompactLeaderRowProps) {
  const pnl = Number(leader.total_realized_pnl_usd || 0) + Number(leader.total_unrealized_pnl_usd || 0);
  const isProfit = pnl >= 0;
  // Some leaders carry a raw wallet (sometimes with a `-<timestamp>` suffix)
  // as their "username" — showing it verbatim spills a 60-char hex string
  // across the row. Detect address-shaped names and collapse them to the
  // same `0x6982…dA165` short form we use when there's no username at all.
  // The `truncate` on the Link only kicks in on overflow (wide screens fit
  // the full string), so this shortening is what actually fixes it.
  const rawName = leader.polymarket_username || "";
  const isAddressName = /^0x[a-fA-F0-9]{40}/.test(rawName);
  const shortAddr = (a: string) => `${a.slice(0, 6)}…${a.slice(-4)}`;
  // The user's private tag (user_trader_labels) wins over any handle.
  const displayName = leader.custom_label?.trim()
    ? leader.custom_label.trim()
    : rawName && !isAddressName
    ? rawName
    : leader.polymarket_address
      ? shortAddr(leader.polymarket_address)
      : isAddressName
        ? shortAddr(rawName)
        : t("portfolio.copy.unknown");
  return (
    <div className="flex items-center gap-3 px-3 py-2.5 rounded-[5px] bg-gray-50 dark:bg-[#18181D] border border-gray-200 dark:border-[#1A1A1A]">
      <LeaderAvatar
        src={leader.profile_image_url}
        address={leader.polymarket_address || leader.polymarket_username}
        size={32}
        alt={leader.polymarket_username || t("portfolio.trader")}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 min-w-0">
          <Link
            href={`/traders/${leader.polymarket_address}`}
            className="font-satoshi font-semibold text-sm text-gray-900 dark:text-white truncate hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            {displayName}
          </Link>
          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-[5px] flex-shrink-0 ${
            leader.copy_enabled
              ? "bg-emerald-500/10 text-emerald-500"
              : "bg-red-500/10 text-red-500"
          }`}>
            {leader.copy_enabled ? t("portfolio.copy.active") : t("portfolio.copy.paused")}
          </span>
        </div>
        <div className="flex items-center gap-2 text-[10px] text-gray-500 dark:text-gray-400 mt-0.5 tabular-nums">
          <span>{leader.total_copied_trades || 0} {t("portfolio.copy.tradesSuffix")}</span>
          <span className="text-gray-300 dark:text-gray-600">·</span>
          <span>{formatCompactCurrency(Number(leader.total_volume_usd) || 0)} {t("portfolio.copy.vol").toLowerCase()}</span>
        </div>
      </div>
      {/* 2026-05-26: Manage button removed per Pika. The Full View link
          in the header (and the /copytrading tab itself) is the single
          entry point for management. Keeping the row chrome-free reads
          cleaner — PnL is the only thing the right column needs to
          surface, and the leader name is still a Link to the trader
          profile for anyone who wants to drill in. */}
      <div className="text-right tabular-nums">
        <div className={`text-sm font-semibold ${isProfit ? "text-emerald-500" : "text-red-500"}`}>
          {isProfit ? "+" : ""}{formatCompactCurrency(pnl)}
        </div>
        <div className="text-[10px] text-gray-500 dark:text-gray-400">
          {t("portfolio.copy.pnlLabel")}
        </div>
      </div>
    </div>
  );
}, (prev, next) => prev.leader === next.leader);

// 2026-05-26: Old LeaderCard component (200 lines: pause/resume/edit/unfollow)
// deleted along with its props interface — see git blame.
// /portfolio?tab=copytrading is now read-only via CompactLeaderRow above; full
// management lives on /copytrading.

// 2026-05-26: Dev-only mock for ?mockCopytrading=1 so the slim-down UI is
// previewable on localhost without real follows. Mirrors the same pattern
// used by ?mockPositions=1 (PositionsSection) and ?mockActivity=1
// (RecentActivitySection). Gated on NODE_ENV !== 'production' so the flag
// has no effect on staging/prod even if it leaks into a URL.
function buildMockFollowing(): FollowedLeader[] {
  const now = Date.now();
  const minutes = (m: number) => new Date(now - m * 60_000).toISOString();
  return [
    {
      id: "mock-1",
      leader_id: "mock-leader-1",
      user_id: "mock",
      copy_enabled: true,
      copy_mode: "percentage",
      base_stake_usd: 10,
      stake_percentage: 5,
      max_stake_per_trade_usd: 50,
      created_at: minutes(60 * 24 * 12),
      polymarket_address: "0x1234567890abcdef1234567890abcdef12345678",
      polymarket_username: "Aragorn",
      profile_image_url: "",
      leader_volume: 142_000,
      leader_win_rate: 64,
      leader_our_volume: 1_840,
      leader_our_win_rate: 71,
      leader_our_wins: 24,
      leader_our_losses: 10,
      last_trade_detected_at: minutes(11),
      total_copied_trades: 34,
      total_volume_usd: 1_840,
      total_realized_pnl_usd: 213.42,
      total_unrealized_pnl_usd: 41.18,
      total_cost_basis_usd: 320,
      open_positions_count: 4,
    },
    {
      id: "mock-2",
      leader_id: "mock-leader-2",
      user_id: "mock",
      copy_enabled: true,
      copy_mode: "fixed",
      base_stake_usd: 25,
      stake_percentage: 0,
      max_stake_per_trade_usd: 100,
      created_at: minutes(60 * 24 * 5),
      polymarket_address: "0xabcdefabcdef1234567890abcdef12345678abcd",
      polymarket_username: "DegenWhale",
      profile_image_url: "",
      leader_volume: 88_000,
      leader_win_rate: 52,
      leader_our_volume: 650,
      leader_our_win_rate: 48,
      leader_our_wins: 11,
      leader_our_losses: 12,
      last_trade_detected_at: minutes(60 * 6),
      total_copied_trades: 23,
      total_volume_usd: 650,
      total_realized_pnl_usd: -47.10,
      total_unrealized_pnl_usd: -12.30,
      total_cost_basis_usd: 180,
      open_positions_count: 2,
    },
    {
      id: "mock-3",
      leader_id: "mock-leader-3",
      user_id: "mock",
      copy_enabled: false,
      copy_mode: "balance_percentage",
      base_stake_usd: 0,
      stake_percentage: 2,
      max_stake_per_trade_usd: 75,
      created_at: minutes(60 * 24 * 30),
      polymarket_address: "0x9999888877776666555544443333222211110000",
      polymarket_username: "SwiftWolf",
      profile_image_url: "",
      leader_volume: 412_000,
      leader_win_rate: 58,
      leader_our_volume: 0,
      leader_our_win_rate: null,
      leader_our_wins: 0,
      leader_our_losses: 0,
      last_trade_detected_at: minutes(60 * 24 * 9),
      total_copied_trades: 0,
      total_volume_usd: 0,
      total_realized_pnl_usd: 0,
      total_unrealized_pnl_usd: 0,
      total_cost_basis_usd: 0,
      open_positions_count: 0,
    },
  ];
}

function CopyTradingSectionContent() {
  const t = useT();
  // 2026-05-26: /portfolio?tab=copytrading is now read-only (CompactLeaderRow).
  // All actions — pause/resume/unfollow/edit/view-trader — moved to /copytrading.
  // Per-leader local state (actionLoading, confirmUnfollow, editingLeader) and
  // the handlers below (handleToggleCopy, handleUnfollow, handleViewTrader,
  // formatPercent) were removed along with the LeaderCard component.
  const isDevMockCopytrading =
    process.env.NODE_ENV !== "production" &&
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("mockCopytrading") === "1";

  const [following, setFollowing] = useState<FollowedLeader[]>(() =>
    isDevMockCopytrading ? buildMockFollowing() : []
  );
  const [loading, setLoading] = useState(!isDevMockCopytrading);
  const [error, setError] = useState<string | null>(null);

  // Real-time WebSocket sync for copytrading state
  const { isConnected: wsConnected } = useCopytradingSocket({
    onLeaderPaused: (event) => {
      // Update local state when leader is paused (from Telegram or another tab)
      setFollowing((prev) =>
        prev.map((leader) =>
          leader.leader_id === event.leaderId
            ? { ...leader, copy_enabled: false }
            : leader
        )
      );
    },
    onLeaderResumed: (event) => {
      // Update local state when leader is resumed (from Telegram or another tab)
      setFollowing((prev) =>
        prev.map((leader) =>
          leader.leader_id === event.leaderId
            ? { ...leader, copy_enabled: true }
            : leader
        )
      );
    },
    onLeaderFollowed: () => {
      // Refresh the list when a new leader is followed
      fetchFollowing();
    },
    onLeaderUnfollowed: (event) => {
      // Remove leader from local state
      setFollowing((prev) =>
        prev.filter((leader) => leader.leader_id !== event.leaderId)
      );
    },
  });

  useEffect(() => {
    // Skip the API fetch when dev-mock is active so the seeded rows aren't
    // overwritten by a real /following call returning an empty list.
    if (isDevMockCopytrading) return;
    fetchFollowing();
  }, [isDevMockCopytrading]);

  const fetchFollowing = async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await authFetch("/api/copytrading/following");
      if (!res.ok) {
        throw new Error("Failed to fetch following");
      }

      const data = await res.json();
      setFollowing(data.following || []);
    } catch (err) {
      logger.error("Error fetching following", { error: err instanceof Error ? err.message : String(err) });
      setError(t("portfolio.copy.error"));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {/* Loading header */}
        <div className="flex items-center gap-2 px-1">
          <div className="w-4 h-4 border-2 border-accent-primary/30 border-t-accent-primary rounded-full animate-spin" />
          <span className="text-xs text-gray-500 dark:text-gray-400">{t("portfolio.copy.loading")}</span>
        </div>
        {/* Stats skeleton */}
        <HorizontalScroll fadeFrom="bg-gray-50 dark:bg-[#18181D]" className="flex sm:grid sm:overflow-visible grid-cols-2 sm:grid-cols-3 gap-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-gray-50 dark:bg-[#18181D] rounded-[5px] p-3 border border-gray-200 dark:border-[#1A1A1A] transition-colors shrink-0 w-[150px] sm:w-auto">
              <div className="h-3 w-16 skeleton-shimmer rounded mb-2" />
              <div className="h-6 w-12 skeleton-shimmer rounded mb-1" />
              <div className="h-3 w-20 skeleton-shimmer rounded" />
            </div>
          ))}
        </HorizontalScroll>
        {/* Card skeletons */}
        {[...Array(2)].map((_, i) => (
          <div
            key={i}
            className="bg-gray-50 dark:bg-[#18181D] rounded-[5px] p-4 border border-gray-200 dark:border-[#1A1A1A] transition-colors"
            style={{ animationDelay: `${i * 100}ms` }}
          >
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 skeleton-shimmer rounded-[5px]" />
              <div className="flex-1">
                <div className="h-5 w-32 skeleton-shimmer rounded mb-2" />
                <div className="h-4 w-48 skeleton-shimmer rounded mb-3" />
                <div className="grid grid-cols-3 gap-2">
                  <div className="h-12 skeleton-shimmer rounded-[5px]" />
                  <div className="h-12 skeleton-shimmer rounded-[5px]" />
                  <div className="h-12 skeleton-shimmer rounded-[5px]" />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 rounded-[5px] p-6 text-red-600 dark:text-red-400 text-sm">
        {error}
      </div>
    );
  }

  if (following.length === 0) {
    return (
      <div className="bg-gray-50 dark:bg-[#18181D] rounded-[5px] p-8 text-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-[5px] bg-gray-200 dark:bg-[#1A1A1A] flex items-center justify-center">
          <FiUser className="w-8 h-8 text-gray-400 dark:text-gray-400" />
        </div>
        <h3 className="font-monument text-lg text-gray-900 dark:text-white mb-2">
          {t("portfolio.copy.emptyTitle")}
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          {t("portfolio.copy.emptySubtitle")}
        </p>
        <Link
          href="/traders"
          className="px-6 py-2.5 rounded-[5px] bg-accent-primary text-accent-foreground font-satoshi font-medium text-sm hover:opacity-90 transition-colors active:scale-[0.98]"
        >
          {t("portfolio.copy.exploreTraders")}
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with Explore button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {t("portfolio.copy.activeOf", { active: following.filter((f) => f.copy_enabled).length, total: following.length })}
          </span>
          {/* Real-time indicator */}
          <div className="flex items-center gap-1" title={wsConnected ? t("portfolio.copy.realtimeOn") : t("portfolio.copy.realtimeOff")}>
            {wsConnected ? (
              <FiWifi className="w-3 h-3 text-green-500" />
            ) : (
              <FiWifiOff className="w-3 h-3 text-yellow-500 animate-pulse" />
            )}
          </div>
        </div>
        {/* 2026-05-26: "Explore Traders" CTA removed per Pika. Full View is
            the only header action — Explore is reachable from the empty
            state below and from the /traders nav, so duplicating it here
            (especially with the accent-primary color) was visual noise. */}
        <div className="flex items-center gap-2">
          <Link
            href="/copytrading"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-[5px] bg-gray-200 dark:bg-[#0f0f12] text-gray-700 dark:text-gray-300 font-inter-tight font-bold text-xs hover:bg-gray-300 dark:hover:bg-[#1A1A1A] transition-colors"
          >
            <FiSliders className="w-3.5 h-3.5" />
            {t("portfolio.copy.fullView")}
          </Link>
        </div>
      </div>

      {/* Summary Stats */}
      {/* 2026-05-22: added 2 cards — Total PnL + Open Positions —
          alongside Copying / Total Trades / Volume. Pika asked because
          "how much have I made copying / how much capital is still
          working" is the headline question for the tab. Grid now
          fits 5 cards: 2-col on mobile, 5-col on lg desktop. */}
      <HorizontalScroll fadeFrom="bg-gray-50 dark:bg-[#18181D]" className="flex sm:grid sm:overflow-visible grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3">
        <div className="bg-gray-50 dark:bg-[#18181D] rounded-[5px] p-3 border border-gray-200 dark:border-[#1A1A1A] transition-colors shrink-0 w-[150px] sm:w-auto">
          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t("portfolio.copy.copying")}</div>
          <div className="font-monument font-bold text-lg text-gray-900 dark:text-white">
            {following.filter((f) => f.copy_enabled).length}
          </div>
          <div className="text-xs text-gray-400 dark:text-gray-400">
            {t("portfolio.copy.ofTraders", { n: following.length })}
          </div>
        </div>
        <div className="bg-gray-50 dark:bg-[#18181D] rounded-[5px] p-3 border border-gray-200 dark:border-[#1A1A1A] transition-colors shrink-0 w-[150px] sm:w-auto">
          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t("portfolio.copy.totalTrades")}</div>
          <div className="font-monument font-bold text-lg text-gray-900 dark:text-white">
            {following.reduce((sum, f) => sum + (f.total_copied_trades || 0), 0)}
          </div>
          <div className="text-xs text-gray-400 dark:text-gray-400">{t("portfolio.copy.copied")}</div>
        </div>
        <div className="bg-gray-50 dark:bg-[#18181D] rounded-[5px] p-3 border border-gray-200 dark:border-[#1A1A1A] transition-colors shrink-0 w-[150px] sm:w-auto">
          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t("portfolio.copy.volume")}</div>
          <div className="font-monument font-bold text-lg text-gray-900 dark:text-white">
            {formatCompactCurrency(following.reduce((sum, f) => sum + (f.total_volume_usd || 0), 0))}
          </div>
          <div className="text-xs text-gray-400 dark:text-gray-400">{t("portfolio.copy.totalLabel")}</div>
        </div>
        <div className="bg-gray-50 dark:bg-[#18181D] rounded-[5px] p-3 border border-gray-200 dark:border-[#1A1A1A] transition-colors shrink-0 w-[150px] sm:w-auto">
          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1 inline-flex items-center gap-1">
            {t("portfolio.copy.pnlLabel")}
            <Tooltip content={t("portfolio.copy.pnlTooltip")}>
              <FiInfo className="w-3 h-3 text-gray-400 dark:text-gray-500 cursor-help" />
            </Tooltip>
          </div>
          <div className={`font-monument font-bold text-lg tabular-nums ${(() => {
            const totalPnl = following.reduce((sum, f) => sum + (f.total_realized_pnl_usd || 0), 0);
            if (totalPnl > 0) return "text-emerald-500";
            if (totalPnl < 0) return "text-red-500";
            return "text-gray-900 dark:text-white";
          })()}`}>
            {(() => {
              const totalPnl = following.reduce((sum, f) => sum + (f.total_realized_pnl_usd || 0), 0);
              return `${totalPnl >= 0 ? "+" : ""}${formatCompactCurrency(totalPnl)}`;
            })()}
          </div>
          <div className="text-xs text-gray-400 dark:text-gray-400">{t("portfolio.copy.netLabel")}</div>
        </div>
        <div className="bg-gray-50 dark:bg-[#18181D] rounded-[5px] p-3 border border-gray-200 dark:border-[#1A1A1A] transition-colors shrink-0 w-[150px] sm:w-auto">
          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t("portfolio.copy.openPositions")}</div>
          <div className="font-monument font-bold text-lg text-gray-900 dark:text-white tabular-nums">
            {following.reduce((sum, f) => sum + (f.open_positions_count || 0), 0)}
          </div>
          <div className="text-xs text-gray-400 dark:text-gray-400">{t("portfolio.copy.liveLabel")}</div>
        </div>
      </HorizontalScroll>

      {/* Compact leader rows (2026-05-26 slim down). Each row is a
          read-only summary with a "Manage" CTA routing to /copytrading
          where the full controls live. Single source of truth for any
          leader-management UX. */}
      {following.length > 0 && (
        <div className="space-y-1.5">
          {following.map((leader) => (
            <CompactLeaderRow key={leader.leader_id} leader={leader} t={t} />
          ))}
        </div>
      )}

      {/* 2026-05-26: CopyTraderModal removed — editing copy-settings now
          lives exclusively on /copytrading > Traders. This pane is a
          read-only summary; the "Manage" link on each CompactLeaderRow
          routes the user there if they want to change anything. */}
    </div>
  );
}

// Wrapper with error boundary
function ErrorBoundaryFallback() {
  const t = useT();
  return (
    <div className="bg-red-50 dark:bg-red-900/20 rounded-[5px] p-6 text-center">
      <FiAlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
      <p className="text-red-600 dark:text-red-400 text-sm font-medium">
        {t("portfolio.copy.errorBoundary")}
      </p>
      <p className="text-red-500/70 dark:text-red-400/70 text-xs mt-1">
        {t("portfolio.copy.errorBoundaryHint")}
      </p>
    </div>
  );
}

export default function CopyTradingSectionWithErrorBoundary() {
  return (
    <ErrorBoundary fallback={<ErrorBoundaryFallback />}>
      <CopyTradingSectionContent />
    </ErrorBoundary>
  );
}
